import Div from "@jumbo/shared/Div/Div";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TabPanel from "app/pages/Docgen/components/common/mui-tabpanel/MuiTabpanel";
import "./ModifyMatterDetails.css";
import FormPreloader from "app/pages/Docgen/utils/FormLoader";
import moment from "moment";
import {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {get, put} from "../../../common/network";
import {getUniqueId} from "../../../utils/CommonMethods";
import RelatedMatters from "../create-matter/RelatedMatters";
import HistoryTab from "./HistoryTab";
import MatterContacts from "./MatterContacts";
import MatterDetails from "./MatterDetails";
import Notes from "./NotesTab";

const MATTER_DATE_FIELDS = ["filing_date", "local_filing_date", "priority_date", "pct_date", "grant_date", "publicationDate", "expiryDate"];

// Backend only accepts MM/DD/YYYY or MM-DD-YYYY; raw fetched values are ISO strings and must be reformatted before every PUT.
const formatMatterDateForApi = date => {
    if (!date) return null;
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format("MM-DD-YYYY") : null;
};

const ModifyMatterDetails = ({handleClose, selectedId}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [note, setNote] = useState("This is a note!");
    const initUniqueId1 = getUniqueId();
    const initUniqueId2 = getUniqueId();
    const [combinedMatterData,setCombinedMatterData] = useState({});
    const [contactsData, setContactsData] = useState();
    const [priorApplicationData, setPriorApplicationData] = useState();
    const [formikObj, setFormikObj] = useState({[initUniqueId1]: {}});
    const [priorFormikObj, setPriorFormikObj] = useState({[initUniqueId2]: {}});
    const [mattersDetails, setMattersDetails] = useState(null);
    const isMountedRef = useRef(true);
    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);
    const [collapse1, setCollapse1] = useState(false);
    const [collapse2, setCollapse2] = useState(false);
    const [loading, setLoading] = useState(false);

    let previousNote = "";
    let transformedPriorApplicationData;

    const formatDate = date => {
        if (!date) return null; // Return empty string if date is null or undefined

        date = typeof date === "string" ? new Date(date + "T00:00:00") : new Date(date);
        const month = date.getMonth() + 1; // Month is zero-indexed, so we add 1
        const day = date.getDate();
        const year = date.getFullYear();

        // Pad single digit month and day with leading zero if needed
        const formattedMonth = month < 10 ? `0${month}` : month;
        const formattedDay = day < 10 ? `0${day}` : day;

        // Format the date as MM-DD-YYYY
        return `${formattedMonth}-${formattedDay}-${year}`;
    };

    const fetchCountryData = async () => {
        const data = await get("docgen/countries");
        let countriesMapping;
        if (data) {
            countriesMapping = data.reduce((acc, country) => {
                acc[country.name] = country.code;
                return acc;
            }, {});
        }
        return countriesMapping;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                let response = await get(`docgen/matters/${selectedId}`);
                delete response.data.country_id;

                if (!isMountedRef.current) {
                    return;
                }
                setMattersDetails(response?.data);

                previousNote = response?.data?.notes;
                setNote(response?.data?.notes);
                let transformedData = (combinedMatterData?.matter_parties ? (combinedMatterData?.matter_parties || []) : response?.data?.matter_parties)?.reduce((acc, record, index) => {
                    const uniqueKey = `${initUniqueId1 - index}`;
                    acc[uniqueKey] = {...record, ...record.party};
                    return acc;
                }, {});

                const transformedPriorData = response?.data?.priorApplications?.reduce((acc, record, index) => {
                    const uniqueKey = `${initUniqueId2 - index}`;
                    acc[uniqueKey] = {...record, ...record.party};
                    return acc;
                }, {});

                const countriesMapping = await fetchCountryData();

                if (!isMountedRef.current) {
                    return;
                }

                transformedPriorApplicationData = Object.keys(transformedPriorData)?.map(key => {
                    const priorApplication = transformedPriorData[key];
                    if (priorApplication?.status !== "") {
                        return {
                            number: priorApplication?.number || "",
                            countryCode: countriesMapping[priorApplication?.countryCode] || priorApplication?.country?.code || "",
                            issueDate: formatDate(priorApplication?.issueDate, true) || null,
                            patentNumber: priorApplication?.patentNumber || "",
                            filingDate: formatDate(priorApplication?.filingDate, true),
                            continuityType: priorApplication?.continuityType || "",
                            status: priorApplication?.status || "",
                            accessCode: priorApplication?.accessCode || ""
                        };
                    }
                }).filter(Boolean);

                setMattersDetails(prevState => ({
                    ...prevState,
                    priorApplications: transformedPriorApplicationData
                }));
                setContactsData(transformedData);
                setFormikObj(transformedData);
                setPriorApplicationData(transformedPriorData);
                setPriorFormikObj(transformedPriorData);
            } catch (error) {
            }
        };

        fetchData();
    }, [selectedId]);


    const setNewNote = async notes => {
        setLoading(true);
        try {
            const formattedDates = MATTER_DATE_FIELDS.reduce((acc, field) => {
                acc[field] = formatMatterDateForApi(mattersDetails?.[field]);
                return acc;
            }, {});
            const combinedMatterDetails = {...mattersDetails,
                    ...combinedMatterData,
                    ...formattedDates,
                    priorApplications: transformedPriorApplicationData
            }
            const response = await put(`docgen/matters/${selectedId}`, {...combinedMatterDetails, notes});
            if (response?.success === true) {
                toast.success(response.message);
            }

        } catch (error) {
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    return (
        <div className="page-content-wrapper">
            <Box className="w-100">
                <div>
                    <Paper>
                        <Div className="matter-details-tab-wrapper">
                            <Tabs
                                value={activeTab}
                                onChange={(event, newValue) => {
                                    setActiveTab(newValue);
                                }}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                aria-label="scrollable force tabs example"
                                className="mui-tab-header"
                            >
                                <Tab label="Docket Details"/>
                                <Tab label="Contacts"/>
                                <Tab label="Related Matters"/>
                                <Tab label="History"/>
                                <Tab label="Notes"/>
                            </Tabs>
                            <TabPanel value={activeTab} index={0}>
                                {mattersDetails ? <MatterDetails setActiveTab={setActiveTab} setFormData={setMattersDetails} priorApplicationData={priorApplicationData} formData={mattersDetails} collapse1={collapse1} setCollapse1={setCollapse1} collapse2={collapse2} setCollapse2={setCollapse2} matterId={selectedId} modify={true} combinedMatterData={combinedMatterData} setCombinedMatterData={setCombinedMatterData}/> : <FormPreloader/>}
                            </TabPanel>
                            <TabPanel value={activeTab} index={1}>
                                {contactsData ? <MatterContacts setActiveTab={setActiveTab} contactsData={contactsData} setContactsData={setContactsData} formikObj={formikObj} setFormikObj={setFormikObj} handleClose={handleClose} matterId={selectedId} modify={true} combinedMatterData={combinedMatterData} setCombinedMatterData={setCombinedMatterData}/> : <FormPreloader/>}
                            </TabPanel>
                            <TabPanel value={activeTab} index={2}>
                                {priorApplicationData ? <RelatedMatters setActiveTab={setActiveTab} priorApplicationData={priorApplicationData} setPriorApplicationData={setPriorApplicationData} formikObj={priorFormikObj} setFormikObj={setPriorFormikObj} handleClose={handleClose} matterId={selectedId} modify={true} setCombinedMatterData={setCombinedMatterData} combinedMatterData={combinedMatterData}/> : <FormPreloader/>}
                            </TabPanel>
                            <TabPanel value={activeTab} index={3}>
                                <HistoryTab id={selectedId}/>
                            </TabPanel>
                            <TabPanel value={activeTab} index={4}>
                                <Notes
                                    note={note}
                                    id={selectedId}
                                    setNote={value => {
                                        setNote(value);
                                    }}
                                    onSubmit={values => {
                                        setNewNote(values);
                                    }}
                                    loading={loading}
                                    handleClose={handleClose}
                                    previousNote={previousNote}
                                />
                            </TabPanel>
                        </Div>
                    </Paper>
                </div>
            </Box>
        </div>
    );
};

export default ModifyMatterDetails;